import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

const URL = 'https://app.inforida.com/api/job';

@Injectable({
  providedIn: 'root',
})
export class JobService {
  constructor(private http: HttpClient) {}

  // Set HTTP Headers
  private headers = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  // CRUD Operations
  createJob(jobData: any): Observable<any> {
    return this.http.post(`${URL}`, jobData, { headers: this.headers });
  }

  getPaginatedJobs(page: number, limit: number): Observable<any> {
    return this.http.get(`${URL}?page=${page}&limit=${limit}`);
  }

  getJobById(id: string): Observable<any> {
    return this.http.get(`${URL}/${id}`);
  }

  updateJob(id: string, jobData: any): Observable<any> {
    return this.http.put(`${URL}/${id}`, jobData, { headers: this.headers });
  }

  deleteJob(id: string): Observable<any> {
    return this.http.delete(`${URL}/${id}`, { headers: this.headers });
  }

  resumeJob(id: string): Observable<any> {
    return this.http.patch(
      `${URL}/resume/${id}`,
      {},
      { headers: this.headers }
    );
  }

  expireJob(id: string): Observable<any> {
    return this.http.patch(
      `${URL}/expire/${id}`,
      {},
      { headers: this.headers }
    );
  }

  // Apply for a Job
  applyForJob(applicationData: any): Observable<any> {
    return this.http.post(`${URL}/apply`, applicationData, {
      headers: this.headers,
    });
  }

  // Track Job View
  trackJobView(id: string): Observable<any> {
    return this.http.patch(`${URL}/view/${id}`, {}, { headers: this.headers });
  }

  // Favorite/Unfavorite a Job
  trackFavoriteJob(favoriteData: any): Observable<any> {
    return this.http.post(`${URL}/favorite`, favoriteData, {
      headers: this.headers,
    });
  }

  // Get Applications for a Job
  getApplicationsForJob(jobId: string): Observable<any> {
    return this.http.get(`${URL}/applications/${jobId}`);
  }

  // Update Application Status
  updateApplicationStatus(applicationStatusData: any): Observable<any> {
    return this.http.patch(`${URL}/application-status`, applicationStatusData, {
      headers: this.headers,
    });
  }

  // Check if Teacher has Applied
  checkIfApplied(jobId: string, email: string): Observable<any> {
    return this.http.get(
      `${URL}/check-application?jobId=${jobId}&email=${email}`
    );
  }

  // Get Jobs by School
  getJobsBySchool(schoolId: string): Observable<any> {
    return this.http.get(`${URL}/school?schoolId=${schoolId}`);
  }

  // Get Jobs by Teacher (applied and favorited)
  getJobsByTeacher(teacherEmail: string): Observable<any> {
    return this.http.get(`${URL}/teacher?email=${teacherEmail}`);
  }

  // Batch Update Jobs
  batchUpdateJobs(batchData: any): Observable<any> {
    return this.http.patch(`${URL}/batch-update`, batchData, {
      headers: this.headers,
    });
  }

  // Schedule a Job Posting
  scheduleJob(scheduleData: any): Observable<any> {
    return this.http.patch(`${URL}/schedule`, scheduleData, {
      headers: this.headers,
    });
  }

  // Advanced Search for Jobs
  advancedSearchJobs(searchParams: any): Observable<any> {
    const query = new URLSearchParams(searchParams).toString();
    return this.http.get(`${URL}/search?${query}`);
  }

  // Filter Jobs
  filterJobs(filterParams: any): Observable<any> {
    const query = new URLSearchParams(filterParams).toString();
    return this.http.get(`${URL}/filter?${query}`);
  }

  // Get Nearby Jobs
  getNearbyJobs(location: string, radius: number): Observable<any> {
    return this.http.get(`${URL}/nearby?location=${location}&radius=${radius}`);
  }

  // Get Popular Jobs by Views or Favorites
  getPopularJobs(metric: string, limit: number = 10): Observable<any> {
    return this.http.get(
      `${URL}/stats/popular?metric=${metric}&limit=${limit}`
    );
  }

  // Get Job Statistics for Employer
  getJobStatsForEmployer(employerId: string): Observable<any> {
    return this.http.get(`${URL}/stats/employer?employerId=${employerId}`);
  }

  // Teacher's Dashboard (Applied and Favorited Jobs)
  getTeacherDashboard(email: string): Observable<any> {
    return this.http.get(`${URL}/dashboard/teacher/${email}`);
  }

  // Test Job API (for testing purpose)
  testJob(testData: any): Observable<any> {
    return this.http.post(`${URL}/test`, testData, { headers: this.headers });
  }
}
